import {
  BorrowingAssetDetail,
  BorrowingDetailActions,
} from "@/components/borrowing/detail/BorrowingAssetDetail";
import BorrowingResponsibilityConfirmation from "@/components/borrowing/BorrowingResponsibilityConfirmation";
import { Button } from "@/components/ui/button/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";
import LoadingIndicator from "@/components/ui/loading/LoadingIndicator";
import { useToast } from "@/lib/hooks/useToast";
import IAsset from "@/models/asset/Asset";
import { useAssetBorrowingStore } from "@/store/asset_borrowing/assetBorrowingStore"; // Assuming you have this store
import { useUserStore } from "@/store/user/userStore";
import axios from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const SubmitAssetBorrowing = () => {
  const { toast } = useToast();
  const assetBorrowingStore = useAssetBorrowingStore();
  const userStore = useUserStore();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isResponsibilityConfirmed, setIsResponsibilityConfirmed] =
    useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formatDateString = (dateString: Date) => {
    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-based
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`; // Format as month/day/year
  };

  const handleConfirmSubmit = () => {
    const borrowingData = assetBorrowingStore.data;
    setLoading(true);

    const date = borrowingData.pick_up_date;
    const date2 = borrowingData.return_date;

    if (!date || !date2) {
      toast({
        title: "Submit error.",
        description: "Please reselect pick up and return dates.",
      });
      return;
    }

    const bDate = formatDateString(date);
    const rDate = formatDateString(date2);

    const formData = new FormData();

    formData.append("check_in_date", rDate);
    formData.append("check_out_date", bDate);
    formData.append("borrow_shift", String(borrowingData.pick_up_shift?.id));
    formData.append("return_shift", String(borrowingData.return_shift?.id));
    formData.append("purpose", borrowingData.purpose);
    formData.append("student_nim_leader", userStore.data?.BinusianId || "");
    formData.append("borrower_email", userStore.data?.Email || "");
    formData.append("borrower_name", userStore.data?.Name || "");
    formData.append("borrower_phone_number", borrowingData.phone);

    const assetIds = convertAssetsToIds(borrowingData.assets);
    formData.append("assets", JSON.stringify(assetIds));

    if (borrowingData.uploaded_file) {
      formData.append("uploaded_file", borrowingData.uploaded_file);
    }

    axios
      .post(
        import.meta.env.VITE_API_URL + "asset-borrowing/student/submit",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      )
      .then(() => {
        setLoading(false);
        assetBorrowingStore.reset();
        localStorage.removeItem("asset_borrowing");
        navigate("/");
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);

        let errMsg = "Failed to submit borrowing request.";
        if (axios.isAxiosError(error)) {
          const resData = error.response?.data;
          const errPayload = resData?.errors;

          if (error.response?.status === 413) {
            errMsg =
              "File too large. Maximum size is 1 MB.";
          } else if (errPayload) {
            if (typeof errPayload === "string") {
              errMsg = errPayload;
            } else if (Array.isArray(errPayload)) {
              errMsg = errPayload.map((e: any) => e.message || (typeof e === 'string' ? e : JSON.stringify(e))).join(", ");
            } else if (typeof errPayload === "object" && errPayload.message) {
              errMsg = String(errPayload.message);
            } else {
              errMsg = JSON.stringify(errPayload);
            }
          } else if (resData?.message && resData.message !== "failed") {
            errMsg = String(resData.message);
          } else if (error.message) {
            errMsg = error.message;
          }
        } else if (error instanceof Error) {
          errMsg = error.message;
        }

        setErrorMessage(errMsg);
        setShowErrorModal(true);
      });

    //Code Before to submit without file upload
    // axios
    //   .post(import.meta.env.VITE_API_URL + "asset-borrowing/student/submit", {
    //     check_in_date: rDate,
    //     check_out_date: bDate,
    //     borrow_shift: borrowingData.pick_up_shift?.id,
    //     return_shift: borrowingData.return_shift?.id,
    //     purpose: borrowingData.purpose,
    //     assets: convertAssetsToIds(borrowingData.assets),
    //     student_nim_leader: userStore.data?.BinusianId,
    //     borrower_email: userStore.data?.Email,
    //     borrower_name: userStore.data?.Name,
    //     borrower_phone_number: borrowingData.phone,
    //   }).then(() => {
    //   setLoading(false);
    //   assetBorrowingStore.reset();
    //   localStorage.removeItem("asset_borrowing");
    //   navigate("/");
    // })
    // .catch((error) => {
    //   setLoading(false);
    //   console.error(error);

    //   toast({
    //     title: "Submit error.",
    //     description: "Failed to submit borrowing request.",
    //   });
    // });

    setShowConfirmation(false);
  };

  // const handleCancelSubmit = () => {
  //   setShowConfirmation(false);
  // };

  const convertAssetsToIds = (assets: IAsset[]): number[] => {
    return assets.map((asset) => asset.id);
  };

  return (
    <>
      <BorrowingAssetDetail
        data={{
          room: {
            has_assets: true,
            id: assetBorrowingStore.data.room,
            room_availability: true,
            room_code: assetBorrowingStore.data.room_code,
            room_name: "",
            room_privacy_category: "Mixed",
            capacity: 0,
          },
          check_in_date: assetBorrowingStore.data.return_date
            ? assetBorrowingStore.data.return_date.toString()
            : "",
          check_out_date: assetBorrowingStore.data.pick_up_date
            ? assetBorrowingStore.data.pick_up_date.toString()
            : "",
          borrow_shift: assetBorrowingStore.data.pick_up_shift?.id,
          return_shift: assetBorrowingStore.data.return_shift?.id,
          purpose: assetBorrowingStore.data.purpose,
          assets: assetBorrowingStore.data.assets,
          borrowing_status: "Request",
          finalize_note: "",
          reason: "",
          student_nim_leader: userStore.data?.BinusianId,
          borrower_email: userStore.data?.Email,
          borrower_phone_number: assetBorrowingStore.data?.phone,
          uploaded_file: assetBorrowingStore.data?.uploaded_file,
          created_at: "",
          id: 0,
        }}
      >
        <LoadingIndicator loading={loading} />
        <BorrowingResponsibilityConfirmation
          checked={isResponsibilityConfirmed}
          onCheckedChange={setIsResponsibilityConfirmed}
          className="mt-4"
        />
        <BorrowingDetailActions>
          <Button
            disabled={loading || !isResponsibilityConfirmed}
            variant={"blue"}
            onClick={() => setShowConfirmation(true)}
          >
            Submit
          </Button>
        </BorrowingDetailActions>
      </BorrowingAssetDetail>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Submission</DialogTitle>
            <DialogDescription>
              Are you sure you want to submit this borrowing request?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="blue" onClick={handleConfirmSubmit}>
              Yes, Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <DialogContent className="sm:max-w-md w-full p-6">
          <DialogHeader className="pr-8">
            <DialogTitle className="text-xl font-semibold text-red-500">
              Submission Error
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 text-sm text-foreground bg-muted/50 p-3 rounded-lg border border-border" style={{ whiteSpace: 'pre-line' }}>
            {errorMessage}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SubmitAssetBorrowing;
